import React, { ReactNode } from "react";
import { Routes, Route, Navigate, NavigateFunction } from "react-router-dom";
import { observer, Provider } from "mobx-react";
import { ItemsStore, RolesAndPermissionStore } from "../shared/store";
import AppRoutes from "./AppRoutes";
import { AuthService } from "../shared/services";
import { Loader, withRouter } from "@mindmemobile/mm-shared-global";
import { IMindMeAccountDetails } from "../shared/interfaces";
import FindAccountRoutes from "./FindAccountRoutes";
import { PageNotFound } from "../shared/components";
import ApplicationSettingsStore from "../shared/store/ApplicationSettingsStore";

class ItemStoreWrapper {
  public itemStore: ItemsStore;
  public rolesAndPermissionStore: RolesAndPermissionStore;
  constructor() {
    this.itemStore = new ItemsStore();
    this.rolesAndPermissionStore = new RolesAndPermissionStore();
  }
}

type IProps = {
  fromMain?: boolean;
  navigate: NavigateFunction;
  location: Location;
};

const itemStoreWrapper = new ItemStoreWrapper();

type IFrameProps = {
  accountId: string;
  subRoute: string;
  mindmeAcountDetails: IMindMeAccountDetails;
};

type IMessageDataProps = {
  origin: string;
  data: IFrameProps;
};

class RoutesConfig extends React.Component<IProps> {
  get fromPaymentSystem(): boolean {
    return this.props.fromMain === true;
  }

  componentDidMount(): void {
    AuthService.setFromPaymentSystem(this.fromPaymentSystem);
    this.readIframeMessage();
  }

  readIframeMessage() {
    window.addEventListener("message", (event: any) => {
      if (!event?.data || typeof event?.data !== "string") return;
      try {
        const data = JSON.parse(event?.data);
        // Ignore Webpack messages
        if (data?.type !== "payment-code") return;
        AuthService.setAuthData(data.accountId);
        AuthService.setFromPaymentSystem(false);
        AuthService.setHideLeftMenu(data.hideLeftMenu);
        AuthService.setHideHeaderBar(data.hideHeaderNav);
        const lastRoute =
          window.location.pathname !== data.navigateToRoute
            ? window.location.pathname
            : "";
        AuthService.setLastRoute(lastRoute);
        AuthService.setMindMeRoute(data.navigateToRoute);
        itemStoreWrapper.rolesAndPermissionStore.setPermissions(
          data.hasViewPermission === true,
          data.hasManagePermission === true
        );
        if (data.mindmeAccountDetails) {
          AuthService.setMindmeAccountDetails(data.mindmeAccountDetails);
        }
        this.props.navigate(data.navigateToRoute);
      } catch (e) {
        console.log("Error parsing data", e);
      }
    });
  }

  componentWillUnmount(): void {
    window.removeEventListener("message", (event: IMessageDataProps) => {
      console.log(" removeEventListener Message from parent:", event.data);
      AuthService.setLogOutState();
    });
  }

  isPurchaseLink(): boolean {
    const isPurchaseLink =
      this.props.location.pathname.includes("app/purchase/checkout") ||
      this.props.location.pathname.includes("app/purchase/purchase-link");
    return isPurchaseLink;
  }

  redirectIfNeeded() {
    const isPurchaseLink = this.isPurchaseLink();

    const domain = window.location.hostname;
    if (domain === "localhost") {
      return;
    }
    const isDomainIsPurchaseAllowedDomain =
      ApplicationSettingsStore.isPurchaseDomainAllowed(domain);

    if (!isPurchaseLink) {
      if (isDomainIsPurchaseAllowedDomain) {
        return this.props.navigate("/page-not-found");
      }
      return;
    }

    const isAllowed = ApplicationSettingsStore.isPurchaseDomainAllowed(domain);

    if (!isAllowed) {
      return this.props.navigate("/page-not-found");
    }
  }

  restrictPublicAccess() {
    const isPurchaseLink = this.isPurchaseLink();

    const isPurchasePreview =
      this.props.location.pathname.includes("/app/checkout/preview/detail") ||
      this.props.location.pathname.includes("/app/checkout/preview"); 

    const isPageNotFound =
      this.props.location.pathname.includes("page-not-found");

    if (isPurchaseLink || isPageNotFound || isPurchasePreview) {
      return false;
    }
    const domain = window.location.hostname;
    if (domain === "localhost") {
      return false;
    }

    //hide for both staging and production
    const isLiveEnv =
      process.env.REACT_APP_IsLive === "true" ||
      process.env.REACT_APP_IsLive === "false";

    if (AuthService.fromPaymentSystem && isLiveEnv) {
      return true;
    }

    return false;
  }

  render(): ReactNode {
    if (ApplicationSettingsStore.isLoadingInProgress) {
      return new Loader().loaderComponent;
    }

    this.redirectIfNeeded();

    const restrictPublicAccess = this.restrictPublicAccess();

    if (restrictPublicAccess) {
      return <>Not allowed</>;
    }

    return (
      <Routes>
        <Route path="/" element={<Navigate to="/app/activity" />} />
        <Route path="/page-not-found" element={<PageNotFound />} />
        <Route path={`/app/find-account/*`} element={<FindAccountRoutes />} />
        <Route
          path={this.fromPaymentSystem ? `/app/*` : "/*"}
          element={
            <Provider {...itemStoreWrapper}>
              <AppRoutes />
            </Provider>
          }
        />
        <Route path="*" element={<Navigate to="/page-not-found" />} />
      </Routes>
    );
  }
}
export default withRouter(observer(RoutesConfig));
